<script setup lang="ts">
    import EchoButton from '@/js/components/EchoButton.vue';
    import ChatMessages from '@/js/components/chat/ChatMessages.vue';
    import ChatTextarea from '@/js/components/chat/ChatTextarea.vue';
    import SuggestedQuestions from '../SuggestedQuestions.vue';
    import {
        ChevronLeftIcon,
        ChevronRightIcon,
        MessageSquareIcon,
        XIcon,
        ZoomInIcon,
    } from '@lucide/vue';

    import { useChatSignalRConnection } from '@/js/composables/useChatSignalRConnection';
    import { IChatMessage } from '@/js/services/api/chat/chat';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import { CustomAxiosRequestConfig, http } from '@/js/services/http';
    import { useAskMeStore } from '@/js/stores/ask-me';
    import { __ } from '@/js/stores/expressions';
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
    import IconButton from '../IconButton.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { AskMeModulePermissionFlag, PermissionArea } from '@/js/consts/permissions';

    const { hasPermission } = usePermissions();
    const canOpenAskMe = hasPermission(
        PermissionArea.AskMeModule,
        AskMeModulePermissionFlag.Access
    );

    const askMe = useAskMeStore();
    const currentView = ref<'chat' | 'suggestions'>('chat');

    const { connection: signalRConnection, switchChat, disconnect } = useChatSignalRConnection();

    signalRConnection.on('ReceiveMessage', (data: IChatMessage) => {
        askMe.receiveChatMessage(data);
    });

    onMounted(() => {
        askMe.clearMessages();
    });

    let navToken = 0;

    onUnmounted(() => {
        navToken++;
        askMe.clearMessages();
        currentView.value = 'chat';
        signalRConnection.off('ReceiveMessage');
        disconnect().catch((err) => console.error('Failed to stop connection:', err));
    });

    watch(
        () => askMe.currentChatId,
        async (chatId, oldChatId) => {
            const token = ++navToken;

            if (askMe.currentChatId) {
                // Keyed on the `'_'` local-echo id, not just "one pending message": a chat-id
                // change can otherwise leave the previous chat's history in place, which
                // mergeFetchedMessages would then wrongly merge into the newly fetched chat.
                if (!(askMe.messages.length === 1 && askMe.messages[0].id === '_')) {
                    askMe.clearMessages();
                }

                const config: CustomAxiosRequestConfig = {};
                if (askMe.messages.length > 0) {
                    askMe.loading = true;
                    config.silent = true;
                }

                http.get(`chat/${chatId}`, config)
                    .then((response) => {
                        if (token !== navToken) return;
                        if (chatId === response.data.id) {
                            const messages = response.data.messages as IChatMessage[];
                            // The fallback poll still covers it even if switchChat below fails.
                            askMe.applyFetchedMessages(response.data.id as string, messages);
                        }
                    })
                    .catch(() => {
                        if (token !== navToken) return;
                        askMe.loading = false;
                    });

                try {
                    await switchChat(chatId, oldChatId, () => token !== navToken);
                } catch (err) {
                    if (token !== navToken) return;
                    console.error('Connection failed:', err);
                }
            }
        },
        { immediate: true }
    );

    const handleSuggestedQuestion = (prompt: string) => {
        if (askMe.loading) {
            return;
        }

        const message: IChatMessage = {
            messageType: 'Text',
            isUserMessage: true,
            message: prompt,
            content: { prompt },
        };

        askMe.addMessageToCurrentChat(message);

        if (!askMe.currentChatId) {
            askMe.createNewChat(message, FilterStoreName.SharedFilter).then((response) => {
                askMe.updateCurrentChatId(response.data.id);
            });
        } else {
            askMe.sendChatMessage(askMe.currentChatId, prompt);
        }

        currentView.value = 'chat';
    };

    const emit = defineEmits<{
        (e: 'closeWindow'): void;
    }>();

    const askMeChatRoute = computed(() =>
        askMe.currentChatId ? `/ask-me/${askMe.currentChatId}` : '/ask-me'
    );
</script>

<template>
    <Teleport to="body">
        <Transition name="slide" appear>
            <div v-show="true" data-modal-wrapper class="fixed inset-0 z-modal pointer-events-none">
                <div
                    class="Chat Chat--modal fixed right-8 bottom-20 w-[383px] h-[545px] max-h-[calc(100vh-6rem)] bg-white border border-gray-lightest shadow-sm rounded-xl pointer-events-auto overflow-hidden"
                >
                    <div class="flex h-full flex-col overflow-hidden">
                        <div
                            class="flex h-[53px] items-center justify-between border-b border-gray-lightest px-6"
                        >
                            <div class="flex items-center gap-1 text-body-bold">
                                <MessageSquareIcon class="size-4 mt-0.25" />
                                {{ __('ask-me') }}
                            </div>

                            <div class="flex gap-1 -mr-5">
                                <IconButton
                                    v-if="canOpenAskMe"
                                    :tooltip="__('open-in-ask-me')"
                                    :route="askMeChatRoute"
                                >
                                    <ZoomInIcon />
                                </IconButton>

                                <IconButton
                                    :tooltip="__('close-modal')"
                                    @click="emit('closeWindow')"
                                >
                                    <XIcon />
                                </IconButton>
                            </div>
                        </div>
                        <template v-if="currentView === 'chat'">
                            <div class="flex justify-end pr-6 pt-2">
                                <EchoButton
                                    variant="tertiary"
                                    size="small"
                                    @click="currentView = 'suggestions'"
                                >
                                    {{ __('ask-me-chat-module-show-questions') }}
                                    <template #icon-right><ChevronRightIcon /></template>
                                </EchoButton>
                            </div>
                            <div class="flex flex-1 flex-col overflow-hidden">
                                <div class="Chat__messages min-h-0 flex-1 overflow-hidden">
                                    <ChatMessages :module="true" />
                                </div>

                                <ChatTextarea
                                    :filter-store-name="FilterStoreName.SharedFilter"
                                    :module="true"
                                />
                            </div>
                        </template>
                        <template v-else>
                            <div class="px-6 pt-4 pb-3">
                                <EchoButton
                                    variant="tertiary"
                                    size="small"
                                    @click="currentView = 'chat'"
                                >
                                    <template #icon-left><ChevronLeftIcon /></template>
                                    {{ __('back-to-chat') }}
                                </EchoButton>
                            </div>
                            <div class="flex-1 overflow-y-auto px-6 pb-6">
                                <SuggestedQuestions @select="handleSuggestedQuestion" />
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
        <Transition name="fade" appear>
            <div
                v-show="true"
                class="fixed inset-0 z-modal-backdrop bg-transparent"
                @click="emit('closeWindow')"
            />
        </Transition>
    </Teleport>
</template>
